/**
 * Utility functions for handling meta tags and generating human-readable filters
 */

/**
 * Title-cases a county name and appends "County" for display,
 * e.g. "el paso" -> "El Paso County". The backend match is case-insensitive.
 */
const formatCountyLabel = (county: string): string =>
  `${county.replace(/\b\w/g, (c) => c.toUpperCase())} County`;

/**
 * Builds the human location label from city/state/county, handling the
 * county-only case (no city) so the title never renders a dangling "in , CO".
 * - city → "City, ST" (+ ", County" when a county is also set)
 * - no city + county → "County, ST"
 * - neither → "ST"
 */
const buildLocationText = (searchParams: any): string => {
  const stateUpper = (searchParams.state || "").toUpperCase();
  const countyLabel = searchParams.county
    ? formatCountyLabel(searchParams.county)
    : "";

  if (searchParams.city) {
    const base = stateUpper
      ? `${searchParams.city}, ${stateUpper}`
      : searchParams.city;
    return countyLabel ? `${base}, ${countyLabel}` : base;
  }
  if (countyLabel) {
    return stateUpper ? `${countyLabel}, ${stateUpper}` : countyLabel;
  }
  return stateUpper;
};

/**
 * Generates human-readable filters and price information from search parameters
 * Returns an object with separate price and other filters
 */
export const generateHumanReadableFilters = (searchParams: any) => {
  // Create separate arrays for different types of filters
  const basicFilters = []; // For bedrooms and bathrooms
  const sizeFilters = []; // For square footage
  const featureFilters = []; // For property features and views
  const locationFilters = []; // For subdivision

  // Variable to hold price information
  let priceInfo = "";

  // Add subdivision filter if available
  if (searchParams.subdivisions && searchParams.subdivisions.length > 0) {
    // Use all subdivisions in the filter with proper formatting
    let subdivisionText = "";
    if (searchParams.subdivisions.length === 1) {
      subdivisionText = `in ${searchParams.subdivisions[0]}`;
    } else if (searchParams.subdivisions.length === 2) {
      subdivisionText = `in ${searchParams.subdivisions[0]} and ${searchParams.subdivisions[1]}`;
    } else {
      // For 3+ subdivisions, use commas and "and" before the last one
      const lastSubdivision =
        searchParams.subdivisions[searchParams.subdivisions.length - 1];
      const otherSubdivisions = searchParams.subdivisions.slice(0, -1);
      subdivisionText = `in ${otherSubdivisions.join(
        ", "
      )} and ${lastSubdivision}`;
    }
    locationFilters.push(subdivisionText);
  } else if (searchParams.subdivision) {
    // Backward compatibility for single subdivision
    locationFilters.push(`in ${searchParams.subdivision}`);
  }

  // Add bedroom and bathroom filters
  if (searchParams.bedrooms) {
    const bedroomText =
      parseInt(searchParams.bedrooms) > 1 ? "Bedrooms" : "Bedroom";
    basicFilters.push(`${searchParams.bedrooms} ${bedroomText}`);
  }

  if (searchParams.bathrooms) {
    const bathroomText =
      parseInt(searchParams.bathrooms) > 1 ? "Bathrooms" : "Bathroom";
    basicFilters.push(`${searchParams.bathrooms} ${bathroomText}`);
  }

  // Generate price information
  if (searchParams.min_price && searchParams.max_price) {
    priceInfo = `priced between $${Number(
      searchParams.min_price
    ).toLocaleString()} and $${Number(
      searchParams.max_price
    ).toLocaleString()}`;
  } else if (searchParams.min_price) {
    priceInfo = `priced from $${Number(
      searchParams.min_price
    ).toLocaleString()}`;
  } else if (searchParams.max_price) {
    priceInfo = `priced under $${Number(
      searchParams.max_price
    ).toLocaleString()}`;
  }

  // Add square footage filters
  if (searchParams.sqft_min && searchParams.sqft_max) {
    sizeFilters.push(
      `sized between ${Number(
        searchParams.sqft_min
      ).toLocaleString()} and ${Number(
        searchParams.sqft_max
      ).toLocaleString()} square feet`
    );
  } else if (searchParams.sqft_min) {
    sizeFilters.push(
      `larger than ${Number(
        searchParams.sqft_min
      ).toLocaleString()} square feet`
    );
  } else if (searchParams.sqft_max) {
    sizeFilters.push(
      `up to ${Number(searchParams.sqft_max).toLocaleString()} square feet`
    );
  }

  // Add all property features as a simple list with capitalized first letters
  const allFeatures = [];

  // Add view features
  if (searchParams.city_view) allFeatures.push("City View");
  if (searchParams.mountain_view) allFeatures.push("Mountain View");
  if (searchParams.park_view) allFeatures.push("Park View");
  if (searchParams.water_view) allFeatures.push("Water View");

  // Add property features
  if (searchParams.waterfront) allFeatures.push("Waterfront");
  if (searchParams.pool) allFeatures.push("Pool");
  if (searchParams.basement) allFeatures.push("Basement");
  if (searchParams.garage) allFeatures.push("Garage");

  if (allFeatures.length > 0) {
    if (allFeatures.length === 1) {
      featureFilters.push(allFeatures[0]);
    } else if (allFeatures.length === 2) {
      featureFilters.push(`${allFeatures[0]} and ${allFeatures[1]}`);
    } else {
      const lastFeature = allFeatures.pop();
      featureFilters.push(`${allFeatures.join(", ")} and ${lastFeature}`);
    }
  }

  // Combine all non-price filters
  const allFilters = [...basicFilters, ...sizeFilters, ...featureFilters];

  // Return price info, other filters, and location filters separately
  return {
    priceInfo,
    otherFilters: allFilters.length > 0 ? allFilters.join(", ") : "",
    locationFilters:
      locationFilters.length > 0 ? locationFilters.join(", ") : "",
  };
};

/**
 * Generates a page title based on search parameters and results
 */
export const generatePageTitle = (
  searchLoading: boolean,
  searchParams: any,
  totalAvailable: number,
  filters?: { priceInfo: string; otherFilters: string; locationFilters: string }
) => {
  // Format the total available number with commas
  const formattedTotal = totalAvailable.toLocaleString();

  // If title is available, use it in the title format
  if (searchParams.title) {
    if (searchLoading) {
      return `Properties in ${buildLocationText(searchParams)}`;
    }

    // Use the title parameter in the "Found X [title]" format
    return `Found ${formattedTotal} ${searchParams.title}`;
  }

  // If no title is available, use the original logic
  // Get property type from searchParams, default to "Homes" if not specified
  const propertyType = searchParams.property_type || "Homes";

  // Format property type for display (capitalize first letter)
  // Special case: Use "Residential" instead of "Single Family Residence" for SEO
  let formattedPropertyType = "";
  if (propertyType === "Single Family Residence") {
    formattedPropertyType = "Residential";
  } else {
    formattedPropertyType =
      propertyType.charAt(0).toUpperCase() + propertyType.slice(1);
  }

  // Construct the property type display
  // If there's a query, include the query and property type
  let propertyDisplay = "";

  if (searchParams.query) {
    // Use the query as is, don't add "Homes" to it
    propertyDisplay = searchParams.query;

    // Add the property type if it's not already in the query and not the default
    if (
      propertyType !== "Homes" &&
      propertyType !== "Single Family Residence" &&
      !searchParams.query.toLowerCase().includes(propertyType.toLowerCase())
    ) {
      propertyDisplay += ` ${formattedPropertyType}`;
    }
  } else {
    // No query, just use the property type
    propertyDisplay = formattedPropertyType;
  }

  // Base title without filters (buildLocationText handles county-only cleanly)
  let title = `Found ${formattedTotal} ${propertyDisplay} in ${buildLocationText(
    searchParams
  )}`;

  // Add subdivision if available
  if (filters?.locationFilters) {
    // When subdivision is present, don't include city name
    title = `Found ${formattedTotal} ${propertyDisplay} ${filters.locationFilters}`;
    // Still reflect the county filter alongside the subdivision
    if (searchParams.county) {
      title += `, ${formatCountyLabel(searchParams.county)}`;
    }
  }

  // Add price info if available
  if (filters?.priceInfo) {
    title += ` ${filters.priceInfo}`;
  }

  // Add other filters if available
  if (filters?.otherFilters) {
    title += ` with ${filters.otherFilters}`;
  }

  return searchLoading
    ? `Properties in ${buildLocationText(searchParams)}`
    : title;
};

/**
 * Generates a page description based on search parameters and results
 */
export const generatePageDescription = (
  searchLoading: boolean,
  searchParams: any,
  totalAvailable: number,
  filters?: { priceInfo: string; otherFilters: string; locationFilters: string }
) => {
  // Format the total available number with commas
  const formattedTotal = totalAvailable.toLocaleString();

  // If title is available, use it instead of property type and ignore other filters
  if (searchParams.title) {
    if (searchLoading) {
      return `Search ${searchParams.title}. Browse current listings and market data.`;
    }

    // Simple description with just the title
    return `Browse all ${formattedTotal} ${searchParams.title}. Click on a property to view prices, photos, and property details.`;
  }

  // If no title is available, use the original logic
  // Get property type from searchParams, default to "Homes" if not specified
  const propertyType = searchParams.property_type || "Properties";

  // Format property type for display (capitalize first letter)
  // Special case: Use "Residential" instead of "Single Family Residence" for SEO
  let formattedPropertyType = "";
  if (propertyType === "Single Family Residence") {
    formattedPropertyType = "Residential";
  } else {
    formattedPropertyType =
      propertyType.charAt(0).toUpperCase() + propertyType.slice(1);
  }

  // Construct the property type display
  // If there's a query, include the query and property type
  let propertyDisplay = "";

  if (searchParams.query) {
    // Use the query as is, don't add "Homes" to it
    propertyDisplay = searchParams.query;

    // Add the property type if it's not already in the query and not the default
    if (
      propertyType !== "Properties" &&
      propertyType !== "Single Family Residence" &&
      !searchParams.query.toLowerCase().includes(propertyType.toLowerCase())
    ) {
      propertyDisplay += ` ${formattedPropertyType}`;
    }
  } else {
    // No query, just use the property type
    propertyDisplay = formattedPropertyType;
  }

  if (searchLoading) {
    return `Search properties in ${buildLocationText(
      searchParams
    )}. Browse current listings and market data.`;
  }

  // Base description without filters (buildLocationText handles county-only)
  let description = `Browse all ${formattedTotal} ${propertyDisplay} in ${buildLocationText(
    searchParams
  )}`;

  // Add subdivision if available
  if (filters?.locationFilters) {
    // When subdivision is present, don't include city name
    description = `Browse all ${formattedTotal} ${propertyDisplay} ${filters.locationFilters}`;
    // Still reflect the county filter alongside the subdivision
    if (searchParams.county) {
      description += `, ${formatCountyLabel(searchParams.county)}`;
    }
  }

  // Add price info if available
  if (filters?.priceInfo) {
    description += ` ${filters.priceInfo}`;
  }

  // Add other filters if available
  if (filters?.otherFilters) {
    description += ` with ${filters.otherFilters}`;
  }

  return `${description}. Click on a property to view prices, photos, and property details.`;
};

/**
 * Updates document meta tags with the provided title and description
 */
export const updateDocumentMetaTags = (
  pageTitle: string,
  pageDescription: string,
  searchParams: any
) => {
  // Update document title
  document.title = pageTitle;

  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", pageDescription);
  }

  // Update meta keywords
  let keywords = searchParams.query
    ? `${searchParams.query}, ${searchParams.city}, ${searchParams.state}, real estate, homes for sale`
    : `${searchParams.city}, ${searchParams.state}, real estate, homes for sale`;

  // Add subdivisions to keywords if available
  if (searchParams.subdivisions && searchParams.subdivisions.length > 0) {
    // Add all subdivisions to keywords
    keywords = `${searchParams.subdivisions.join(", ")}, ${keywords}`;
  } else if (searchParams.subdivision) {
    // Backward compatibility for single subdivision
    keywords = `${searchParams.subdivision}, ${keywords}`;
  }

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement("meta");
    metaKeywords.setAttribute("name", "keywords");
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute("content", keywords);

  // Update Open Graph meta tags
  updateMetaTag("og:title", pageTitle);
  updateMetaTag("og:description", pageDescription);
  updateMetaTag("og:type", "website");
  updateMetaTag("twitter:card", "summary_large_image");
  updateMetaTag("twitter:title", pageTitle);
  updateMetaTag("twitter:description", pageDescription);

  // Update canonical link
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement("link");
    canonicalLink.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute("href", window.location.href);
};

/**
 * Helper function to update a specific meta tag
 */
const updateMetaTag = (name: string, content: string) => {
  let metaTag = document.querySelector(
    `meta[${name.includes(":") ? "property" : "name"}="${name}"]`
  );
  if (!metaTag) {
    metaTag = document.createElement("meta");
    metaTag.setAttribute(name.includes(":") ? "property" : "name", name);
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute("content", content);
};
